import React from 'react'
import type { LiveTip, TipStatus } from '../types'
import { useOdds } from '../context/OddsContext'
import { IoChevronDown, IoChevronUp, IoCheckmarkCircle, IoCloseCircle, IoAlertCircle, IoTime } from 'react-icons/io5'

interface LiveTipCardProps {
  tip: LiveTip
  isExpanded: boolean
  onToggle: () => void
}

function getStatusBadge(status: TipStatus, settledResult?: string | null) {
  switch (status) {
    case 'active':
      return { label: 'Active', className: 'status-active', icon: <IoTime size={12} /> }
    case 'landed':
      return { label: 'Landed', className: 'status-landed', icon: <IoCheckmarkCircle size={12} /> }
    case 'missed':
      return { label: 'Missed', className: 'status-missed', icon: <IoCloseCircle size={12} /> }
    case 'invalidated':
      return { label: 'Void', className: 'status-void', icon: <IoAlertCircle size={12} /> }
    case 'expired':
      return { label: 'Expired', className: 'status-expired', icon: <IoTime size={12} /> }
    default:
      return { label: status, className: 'status-unknown', icon: null }
  }
}

function getConfidenceStyle(confidence: string): React.CSSProperties {
  switch (confidence) {
    case 'high':
      return {
        background: 'rgba(74, 222, 128, 0.15)',
        borderColor: 'rgba(74, 222, 128, 0.4)',
        color: 'var(--green)',
      }
    case 'med':
      return {
        background: 'rgba(251, 191, 36, 0.15)',
        borderColor: 'rgba(251, 191, 36, 0.4)',
        color: 'var(--yellow)',
      }
    default:
      return {
        background: 'rgba(251, 113, 133, 0.15)',
        borderColor: 'rgba(251, 113, 133, 0.4)',
        color: 'var(--red)',
      }
  }
}

function formatMatchScore(home: number | null, away: number | null): string {
  if (home === null || away === null) return '—'
  return `${home} - ${away}`
}

function formatElapsed(minute: number): string {
  return `${minute}'`
}

export function LiveTipCard({ tip, isExpanded, onToggle }: LiveTipCardProps) {
  const { formatOdds } = useOdds()
  const statusBadge = getStatusBadge(tip.status, tip.settled_result)
  const confStyle = getConfidenceStyle(tip.confidence)

  // Calculate edge if we have odds
  const edge = tip.best_odds && tip.implied_probability
    ? tip.pre_match_probability - tip.implied_probability
    : null

  return (
    <div
      className={`live-tip-card ${isExpanded ? 'expanded' : ''} ${tip.status}`}
      onClick={onToggle}
    >
      {/* Compact Header - Always Visible */}
      <div className="live-tip-compact">
        <div className="live-tip-main">
          {/* Status Badge */}
          <span className={`status-badge ${statusBadge.className}`}>
            {statusBadge.icon}
            <span>{statusBadge.label}</span>
          </span>

          {/* Selection Info */}
          <div className="live-tip-selection">
            <span className="selection-label">{tip.selection_label}</span>
            <span className="fixture-info">
              <span className="teams">{tip.home_team} vs {tip.away_team}</span>
              <span className="match-context">
                <span className="score">{formatMatchScore(tip.match_score_home, tip.match_score_away)}</span>
                <span className="elapsed">{formatElapsed(tip.elapsed_minute)}</span>
              </span>
            </span>
          </div>

          {/* Probability & Odds */}
          <div className="live-tip-data">
            <span
              className="pill probability-pill"
              style={{
                ...confStyle,
                border: `1px solid ${confStyle.borderColor}`,
              }}
              title={`Pre-match probability: ${tip.pre_match_probability}%`}
            >
              {tip.pre_match_probability}%
            </span>
            {tip.best_odds && (
              <div className="odds-info">
                <span className="odds-value">{formatOdds(tip.best_odds)}</span>
                {tip.best_bookmaker_name && (
                  <span className="bookmaker-name">{tip.best_bookmaker_name}</span>
                )}
              </div>
            )}
          </div>

          {/* Expand Icon */}
          <span className="expand-icon">
            {isExpanded ? <IoChevronUp size={16} /> : <IoChevronDown size={16} />}
          </span>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="live-tip-details">
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Market</span>
              <span className="detail-value">{tip.market_category}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Pre-Match Probability</span>
              <span className="detail-value">{tip.pre_match_probability}%</span>
            </div>
            {tip.implied_probability && (
              <div className="detail-row">
                <span className="detail-label">Implied Probability</span>
                <span className="detail-value">{tip.implied_probability.toFixed(1)}%</span>
              </div>
            )}
            {edge !== null && (
              <div className="detail-row">
                <span className="detail-label">Edge</span>
                <span className={`detail-value ${edge >= 0 ? 'edge-positive' : 'edge-negative'}`}>
                  {edge >= 0 ? '+' : ''}{edge.toFixed(1)}%
                </span>
              </div>
            )}
            {tip.player_name && (
              <div className="detail-row">
                <span className="detail-label">Player</span>
                <span className="detail-value">{tip.player_name}</span>
              </div>
            )}
            {tip.line !== null && (
              <div className="detail-row">
                <span className="detail-label">Line</span>
                <span className="detail-value">{tip.outcome} {tip.line}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Generated At</span>
              <span className="detail-value">{formatElapsed(tip.elapsed_minute)} into match</span>
            </div>
            {tip.status === 'invalidated' && tip.invalidation_reason && (
              <div className="detail-row">
                <span className="detail-label">Invalidated</span>
                <span className="detail-value invalidation-reason">{tip.invalidation_reason}</span>
              </div>
            )}
            {tip.settled_minute && (
              <div className="detail-row">
                <span className="detail-label">Settled At</span>
                <span className="detail-value">{formatElapsed(tip.settled_minute)}</span>
              </div>
            )}
          </div>

          {tip.why && (
            <div className="detail-why">
              <span className="why-label">Analysis:</span>
              <span className="why-text">{tip.why}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
